import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress,
    Alert, Stack, Checkbox, FormControlLabel
} from '@mui/material';
import * as api from './accountsApi';
import type { Account } from './accountsApi';
import { sx14, paperSx, FormRow, DetailHeader } from '../common/detailView';

interface AccountViewProps {
    accountId: number | null;
}

export function AccountView({ accountId }: AccountViewProps) {
    const { t } = useTranslation();

    const [account, setAccount] = useState<Account | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isNewAccount, setIsNewAccount] = useState(false);
    const [accountCopy, setAccountCopy] = useState<Account | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isNew = accountId === null;

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            if (isNew) {
                const newAccount: Account = {
                    id: 0,
                    accountNumber: '',
                    accountName: '',
                    isRoundingAccount: false,
                };
                setAccount(newAccount);
                setIsNewAccount(true);
                setIsEditing(true);
            } else {
                const data = await api.getAccount(accountId!);
                setAccount(data);
                setIsNewAccount(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [accountId, isNew]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }

    if (error && !account) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!account) return null;

    const isAccountNumberValid = (): boolean => !!account.accountNumber && account.accountNumber.length > 0;

    const startEdit = () => {
        setAccountCopy(JSON.parse(JSON.stringify(account)));
        setIsEditing(true);
    };

    const cancelEdit = () => {
        if (accountCopy) setAccount(accountCopy);
        setIsEditing(false);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            if (isNewAccount) {
                const response = await api.createAccount(account);
                setAccount(response);
                setIsNewAccount(false);
                setIsEditing(false);
            } else {
                const response = await api.updateAccount(account);
                setAccount(response);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const deleteAccount = async () => {
        if (!window.confirm(t('account.confirmDelete'))) return;
        setSaving(true);
        setError(null);
        try {
            await api.deleteAccount(account.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const updateAccountField = <K extends keyof Account>(field: K, value: Account[K]) => {
        setAccount({ ...account, [field]: value });
    };

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${account.accountNumber} ${account.accountName}`}
                isEditing={isEditing}
                isNew={isNewAccount}
                saving={saving}
                canSave={isAccountNumberValid()}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={deleteAccount}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('account.accountNumber')}>
                        {!isNewAccount && !isEditing ? (
                            <Typography sx={sx14}>{account.accountNumber}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                value={account.accountNumber}
                                onChange={e => updateAccountField('accountNumber', e.target.value.toUpperCase())}
                                error={!isAccountNumberValid()}
                            />
                        )}
                    </FormRow>

                    <FormRow label={t('account.accountName')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{account.accountName}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                inputProps={{ maxLength: 100 }}
                                value={account.accountName}
                                onChange={e => updateAccountField('accountName', e.target.value)}
                            />
                        )}
                    </FormRow>

                    <FormRow label={t('account.isRoundingAccount')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>
                                {account.isRoundingAccount ? t('common.true') : t('common.false')}
                            </Typography>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={account.isRoundingAccount}
                                        onChange={e => updateAccountField('isRoundingAccount', e.target.checked)}
                                    />
                                }
                                label=""
                            />
                        )}
                    </FormRow>
                </Stack>
            </Box>

            {saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    );
}
